import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { setTicketContextRef } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TicketContext = createContext(null);

export const useTickets = () => {
  const context = useContext(TicketContext);
  if (!context) {
    throw new Error('useTickets must be used within a TicketProvider');
  }
  return context;
};

export const TicketProvider = ({ children }) => {
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketData, setTicketData] = useState({
    errorMessage: '',
    errorStack: '',
    pageUrl: '',
    component: '',
  });
  const [userTickets, setUserTickets] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Ref for providing context to API interceptor
  const contextRef = useRef(null);

  // Open ticket modal with pre-filled error data
  const openTicketModal = useCallback((errorInfo = {}) => {
    setTicketData({
      errorMessage: errorInfo.errorMessage || '',
      errorStack: errorInfo.errorStack || '',
      pageUrl: errorInfo.pageUrl || window.location.href,
      component: errorInfo.component || '',
    });
    setShowTicketModal(true);
  }, []);
  
  // Register context with API interceptor
  useEffect(() => {
    contextRef.current = { openTicketModal };
    setTicketContextRef(contextRef.current);
    
    return () => {
      setTicketContextRef(null);
    };
  }, [openTicketModal]);

  // Close ticket modal
  const closeTicketModal = useCallback(() => {
    setShowTicketModal(false);
    setTicketData({
      errorMessage: '',
      errorStack: '',
      pageUrl: '',
      component: '',
    });
  }, []);

  // Submit a new ticket
  const submitTicket = useCallback(async (ticketInfo) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/tickets/create`,
        {
          title: ticketInfo.title,
          description: ticketInfo.description,
          error_message: ticketInfo.errorMessage || ticketData.errorMessage,
          error_stack: ticketInfo.errorStack || ticketData.errorStack,
          page_url: ticketInfo.pageUrl || ticketData.pageUrl || window.location.href,
          component: ticketInfo.component || ticketData.component,
          category: ticketInfo.category || 'general',
          priority: ticketInfo.priority || 'medium',
          browser_info: navigator.userAgent,
          additional_context: ticketInfo.additionalContext,
        },
        { withCredentials: true }
      );
      
      closeTicketModal();
      return response.data;
    } catch (error) {
      console.error('Failed to submit ticket:', error);
      throw error;
    }
  }, [ticketData, closeTicketModal]);

  // Quick report (auto-creates ticket from error)
  const quickReportError = useCallback(async (errorMessage, component = '') => {
    try {
      const response = await axios.post(
        `${API_URL}/api/tickets/quick-report`,
        null,
        {
          params: {
            error_message: errorMessage,
            page_url: window.location.href,
            component: component,
          },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to quick report error:', error);
      return null;
    }
  }, []);

  // Fetch user's tickets
  const fetchUserTickets = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/tickets/my-tickets`,
        { withCredentials: true }
      );
      setUserTickets(response.data.tickets || []);
      
      // Count tickets with new admin responses
      const unread = (response.data.tickets || []).filter(t => {
        const lastResponse = t.responses?.[t.responses.length - 1];
        return lastResponse?.from === 'admin' && t.status !== 'closed';
      }).length;
      setUnreadCount(unread);
      
      return response.data;
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      return { tickets: [] };
    }
  }, []);

  // Add response to a ticket
  const respondToTicket = useCallback(async (ticketId, message, content) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/tickets/my-tickets/${ticketId}/respond`,
        { message, content },
        { withCredentials: true }
      );
      await fetchUserTickets();
      return response.data;
    } catch (error) {
      console.error('Failed to respond to ticket:', error);
      throw error;
    }
  }, [fetchUserTickets]);

  const value = {
    showTicketModal,
    ticketData,
    userTickets,
    unreadCount,
    openTicketModal,
    closeTicketModal,
    submitTicket,
    quickReportError,
    fetchUserTickets,
    respondToTicket,
  };

  return (
    <TicketContext.Provider value={value}>
      {children}
    </TicketContext.Provider>
  );
};

export default TicketContext;
